<template>
  <div>
    <v-row
      v-if="!requestCompleted"
      align="center"
      justify="center"
      class="ma-0"
    >
      <v-col
        class="text-center pa-0"
        cols="12"
        sm="10"
        md="8"
        lg="6"
        xl="4"
      >
        <h1
          class="mt-10 mb-4 display-1"
        >
          {{ $t('globals.resetPassword') }}
        </h1>
        <p
          class="mt-6 ps-2 pe-2"
        >
          <span
            v-if="!tokenVerified"
          >
            {{ $t('resetPassword.intro') }}
          </span>
          <span
            v-if="tokenVerified"
          >
            {{ $t('resetPassword.introPassword') }}
          </span>
        </p>

        <v-card
          class="mx-auto pa-6 ma-0 mt-8"
          flat
        >
          <v-form
            v-if="!tokenVerified"
            ref="form"
            lazy-validation
            @submit.prevent="requestPasswordResetEmail"
          >
            <v-text-field
              v-model="form.email"
              :error-messages="emailErrors"
              :class="{ 'input--error': $v.form.email.$error }"
              :label="$t('globals.email')"
              :disabled="requestLoading"
              required
              outlined
              @keydown="error = false; $v.form.email.$reset()"
              @blur="$v.form.email.$touch()"
            />
            <v-btn
              type="submit"
              class="mt-2"
              large
              color="primary"
            >
              <i
                v-if="!requestLoading"
                class="me-2 v-icon icon-lock-reset"
              />
              <v-progress-circular
                v-if="requestLoading"
                :size="24"
                :width="3"
                class="me-2"
                indeterminate
                color="white"
              />
              {{ $t('resetPassword.sendRequest') }}
            </v-btn>
          </v-form>

          <Alert v-if="error" class="mt-2" :dismissible="true" :message="$t('errorMessages.resetPasswordFailed')" type="error" />

          <v-form
            v-if="tokenVerified"
            ref="formPassword"
            lazy-validation
            @submit.prevent="requestPasswordReset"
          >
            <v-text-field
              v-model="formPassword.password"
              :error-messages="passwordErrors"
              :class="{ 'input--error': $v.formPassword.password.$error }"
              :label="$t('resetPassword.newPassword')"
              :disabled="resetLoading"
              type="password"
              required
              outlined
              @keydown="error = false; $v.form.password.$reset()"
              @blur="$v.formPassword.password.$touch()"
            />
            <v-btn
              type="submit"
              class="mt-2"
              large
              color="primary"
            >
              <i
                v-if="!resetLoading"
                class="me-2 v-icon icon-lock-reset"
              />
              <v-progress-circular
                v-if="resetLoading"
                :size="24"
                :width="3"
                class="me-2"
                indeterminate
                color="white"
              />
              {{ $t('resetPassword.setNewPassword') }}
            </v-btn>
          </v-form>
        </v-card>
        <p
          class="mt-8"
        >
          <nuxt-link
            :to="localePath('index')"
          >
            {{ $t('globals.backToHome') }}
          </nuxt-link>
        </p>
      </v-col>
    </v-row>

    <v-row
      v-if="requestCompleted"
      align="center"
      justify="center"
    >
      <v-col
        class="pa-6 text-center"
        cols="12"
        sm="10"
        md="8"
        lg="6"
        xl="4"
      >
        <h1
          class="mt-10 mb-4 display-1"
        >
          {{ $t('globals.resetPassword') }}
        </h1>
        <Alert
          v-if="requestEmailRequestCompleted"
          :title="$t('resetPassword.confirmationTitle')"
          :message="$t('resetPassword.confirmationDesc')"
          class="pa-6 text-center mt-8"
          type="success"
        />
        <Alert
          v-if="resetPasswordRequestCompleted"
          :title="$t('resetPassword.confirmationTitle')"
          :message="$t('resetPassword.confirmationPasswordDesc')"
          class="pa-6 text-center mt-8"
          type="success"
        />
        <p
          class="mt-8"
        >
          <nuxt-link
            v-if="requestEmailRequestCompleted"
            :to="localePath('index')"
          >
            {{ $t('globals.backToHome') }}
          </nuxt-link>

          <nuxt-link
            v-if="resetPasswordRequestCompleted"
            :to="localePath('account-login')"
          >
            {{ $t('globals.login') }}
          </nuxt-link>
        </p>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { required, email, minLength } from 'vuelidate/lib/validators'
import { scrollToFirstError } from '@/utils'
import Alert from '@/components/Alert'

export default {
  auth: 'guest',

  components: {
    Alert
  },

  validations: {
    form: {
      email: {
        required,
        email
      }
    },
    formPassword: {
      password: {
        required,
        minLength: minLength(8)
      }
    }
  },

  data () {
    return {
      form: {
        email: ''
      },
      formPassword: {
        password: ''
      },
      error: false,
      requestCompleted: false,
      requestEmailRequestCompleted: false,
      resetPasswordRequestCompleted: false,
      requestLoading: false,
      resetLoading: false,
      token: '',
      tokenVerified: false
    }
  },

  head () {
    return {
      htmlAttrs: {
        dir: this.$t('meta.dir')
      },
      title: `${this.$t('globals.siteName')} — ${this.$t('globals.resetPassword')}`,
      meta: [
        { hid: 'og:title', property: 'og:title', content: `${this.$t('globals.siteName')} — ${this.$t('globals.resetPassword')}` },
        { hid: 'description', name: 'description', content: this.$t('resetPassword.description') },
        { hid: 'og:description', property: 'og:description', content: this.$t('resetPassword.description') },
        { hid: 'og:image', property: 'og:image', content: `${process.env.SITE_URL}/img/siteimage.jpg` },
        { hid: 'twitter:image', name: 'twitter:image', content: `${process.env.SITE_URL}/img/siteimage.jpg` }
      ]
    }
  },

  computed: {
    emailErrors () {
      const errors = []
      if (!this.$v.form.email.$dirty) {
        return errors
      }
      !this.$v.form.email.email && errors.push(this.$t('errorMessages.emailInvalid'))
      !this.$v.form.email.required && errors.push(this.$t('errorMessages.emailRequired'))
      return errors
    },
    passwordErrors () {
      const errors = []
      if (!this.$v.formPassword.password.$dirty) {
        return errors
      }
      !this.$v.formPassword.password.required && errors.push(this.$t('errorMessages.passwordRequired'))
      !this.$v.formPassword.password.minLength && errors.push(this.$t('errorMessages.passwordLengthNotCorrect'))
      return errors
    }
  },

  created () {
    this.$store.commit('updateBreadcrumbs', [])
  },

  async mounted () {
    if (this.$route.query.token) {
      this.tokenVerified = true
      this.token = this.$route.query.token
    }
    await this.$recaptchaLoaded()
  },

  methods: {
    async requestPasswordResetEmail () {
      await this.$v.form.$touch()
      this.$nextTick(() => {
        scrollToFirstError()
      })
      if (!this.$v.form.$error) {
        this.requestLoading = true
        this.error = false
        try {
          const recaptchaToken = await this.$recaptcha('sendResetPassword')
          await this.$axios.$post('/users/send-reset-password', {
            recaptcha_token: recaptchaToken,
            email: this.form.email.trim()
          })
          this.requestCompleted = true
          this.requestEmailRequestCompleted = true
        } catch (recaptchaError) {
          this.requestLoading = false
          this.error = true
        }
      }
    },
    async requestPasswordReset () {
      await this.$v.formPassword.$touch()
      this.$nextTick(() => {
        scrollToFirstError()
      })
      if (!this.$v.formPassword.$error) {
        this.resetLoading = true
        this.error = false
        try {
          this.$axios.$post(`/users/reset-password/${this.token}`, {
            password: this.formPassword.password
          }).then(async (response) => {
            await this.$auth.setToken('local', `Bearer ${response.token}`)
            await this.$auth.setStrategy('local')
            this.requestCompleted = true
            this.resetPasswordRequestCompleted = true
          })
        } catch (error) {
          this.resetLoading = false
          this.error = true
        }
      }
    }
  }

}
</script>
<style lang="scss" scoped>
.v-card {
  border-radius: $standard-radius;
}
</style>
